// Products.js
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import * as React from "react";
import CreateProduct from "./CreateProduct";
import UpdateProduct from "./UpdateProduct";
export default function Products({
  products,
  currentPage,
  totalPages,
  onPageChange,
  onProductUpdated,
}) {
  const [deletingProduct, setDeletingProduct] = React.useState(null);
  const [editingProduct, setEditingProduct] = React.useState(null);
  const [viewingReviewsProduct, setViewingReviewsProduct] =
    React.useState(null);
  const [reviews, setReviews] = React.useState([]);
  const [averageRating, setAverageRating] = React.useState(0);
  const [totalReviews, setTotalReviews] = React.useState(0);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [keywords, setKeywords] = React.useState("");
  const [selectedActiveStatuses, setSelectedActiveStatuses] = React.useState(
    [],
  );
  const [selectedStores, setSelectedStores] = React.useState([]);
  const [selectedRatingRanges, setSelectedRatingRanges] = React.useState([]);
  const [productRatings, setProductRatings] = React.useState({});
  const [creatingProduct, setCreatingProduct] = React.useState(false);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  // Active/Inactive statuses for filter
  const activeStatuses = ["active", "inactive"];
  const handleDeleteProduct = async () => {
    if (!deletingProduct) return;

    try {
      const response = await axios.delete(
        `https://detoxtea-fullstack.onrender.com/api/admin/products/${deletingProduct._id}`,
        {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("accessToken") || ""
            }`,
          },
        },
      );

      if (response.status === 200) {
        setSnackbar({
          open: true,
          msg: "Product deleted successfully!",
          severity: "success",
        });
        setDeletingProduct(null);
        onProductUpdated(currentPage); // Reload current page
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Delete error:", error.response || error);
      setSnackbar({
        open: true,
        msg: `Error deleting product! ${
          error.response?.data?.message || error.message
        }`,
        severity: "error",
      });
      setDeletingProduct(null);
    }
  };

  const handleViewReviews = async (product) => {
    try {
      const res = await axios.get(
        `https://detoxtea-fullstack.onrender.com/api/admin/products/${product._id}/reviews`,
        {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("accessToken") || ""
            }`,
          },
        },
      );
      setReviews(res.data.data);
      setAverageRating(res.data.averageRating);
      setTotalReviews(res.data.totalReviews);
      setViewingReviewsProduct(product);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setSnackbar({
        open: true,
        msg: "Error fetching product reviews!",
        severity: "error",
      });
    }
  };

  const handleCloseReviewsDialog = () => {
    setViewingReviewsProduct(null);
    setReviews([]);
  };

  // Define rating ranges
  const ratingRanges = [
    { label: ">4", min: 4, max: 5.1 },
    { label: "3 < =<4", min: 3, max: 4 },
    { label: "2 < =<3", min: 2, max: 3 },
    { label: "=<2", min: 0, max: 2 },
  ];

  // Fetch ratings for products
  React.useEffect(() => {
    const fetchRatings = async () => {
      const ratings = {};
      await Promise.all(
        products.map(async (product) => {
          try {
            const res = await axios.get(
              `https://detoxtea-fullstack.onrender.com/api/admin/products/${product._id}/reviews`,
              {
                headers: {
                  Authorization: `Bearer ${
                    localStorage.getItem("accessToken") || ""
                  }`,
                },
              },
            );
            ratings[product._id] = {
              averageRating: parseFloat(res.data.averageRating),
              totalReviews: res.data.totalReviews,
            };
          } catch (error) {
            console.error(
              `Error fetching ratings for product ${product._id}:`,
              error,
            );
            ratings[product._id] = { averageRating: 0, totalReviews: 0 };
          }
        }),
      );
      setProductRatings(ratings);
    };
    fetchRatings();
  }, [products]);

  // Filter products based on search, stores, and rating ranges
  const filteredProducts = React.useMemo(() => {
    let filtered = [...products.map((product) => ({ ...product }))]; // Copy to modify

    filtered = filtered.map((product) => {
      const ratingInfo = productRatings[product._id] || {
        averageRating: 0,
        totalReviews: 0,
      };
      product.averageRating = ratingInfo.averageRating;
      product.totalReviews = ratingInfo.totalReviews;
      return product;
    });

    // 1. Filter by search (title or description)
    if (keywords.trim() !== "") {
      const keywordLower = keywords.trim().toLowerCase();
      filtered = filtered.filter(
        (product) =>
          (product.title &&
            product.title.toLowerCase().includes(keywordLower)) ||
          (product.description &&
            product.description.toLowerCase().includes(keywordLower)),
      );
    }

    // 2. Filter by active status (isAuction field)
    if (selectedActiveStatuses.length > 0) {
      filtered = filtered.filter((product) => {
        if (selectedActiveStatuses.includes("active") && product.isAuction) {
          return true;
        }
        if (selectedActiveStatuses.includes("inactive") && !product.isAuction) {
          return true;
        }
        return false;
      });
    }

    // 4. Filter by selected rating ranges
    if (selectedRatingRanges.length > 0) {
      filtered = filtered.filter((product) => {
        const rating = product.averageRating || 0;
        return selectedRatingRanges.some((rangeLabel) => {
          const range = ratingRanges.find((r) => r.label === rangeLabel);
          return rating > range.min && rating <= range.max;
        });
      });
    }

    return filtered;
  }, [
    products,
    keywords,
    selectedActiveStatuses,
    selectedRatingRanges,
    productRatings,
  ]);

  React.useEffect(() => {
    // Reset to first page when filters change
  }, [selectedActiveStatuses, selectedRatingRanges, keywords]);

  const handleActiveStatusChange = (status) => {
    setSelectedActiveStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const handleRatingRangeChange = (rangeLabel) => {
    setSelectedRatingRanges((prev) =>
      prev.includes(rangeLabel)
        ? prev.filter((r) => r !== rangeLabel)
        : [...prev, rangeLabel],
    );
  };

  return (
    <React.Fragment>
      <Dialog
        open={Boolean(deletingProduct)}
        onClose={() => setDeletingProduct(null)}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <DeleteIcon color="error" sx={{ mr: 1 }} />
            Confirm Product Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete product{" "}
            <b>{deletingProduct?.title}</b> This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingProduct(null)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProduct}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.msg}</Alert>
      </Snackbar>

      {/* Reviews Dialog */}
      <Dialog
        open={Boolean(viewingReviewsProduct)}
        onClose={handleCloseReviewsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reviews for {viewingReviewsProduct?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="h6">
            Average Rating: {averageRating} ({totalReviews} reviews)
          </Typography>
          <List>
            {reviews.map((review) => (
              <ListItem key={review._id} divider>
                <ListItemText
                  primary={`${review.rating} stars - ${
                    review.reviewerId?.username || "Anonymous"
                  }`}
                  secondary={review.comment}
                />
              </ListItem>
            ))}
            {reviews.length === 0 && <Typography>No reviews yet.</Typography>}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReviewsDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              borderRadius: 3,
              bgcolor: "background.paper",
              p: 2,
              mb: 3,
              minWidth: 200,
              maxWidth: 260,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                letterSpacing={2}
              >
                <FilterAltIcon
                  fontSize="small"
                  sx={{ mr: 0.5, color: "primary.main" }}
                />
                FILTERS
              </Typography>
              {(selectedActiveStatuses.length > 0 ||
                selectedStores.length > 0 ||
                selectedRatingRanges.length > 0) && (
                <Tooltip title="Clear all">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedActiveStatuses([]);
                      setSelectedRatingRanges([]);
                      setKeywords("");
                    }}
                  >
                    <ClearAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Search */}
            <Box mb={2}>
              <TextField
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                size="small"
                fullWidth
                label="Search by title/description"
                InputProps={{ endAdornment: <SearchIcon /> }}
              />
            </Box>

            {/* Active Status Filter */}
            <Box mb={2}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                ACTIVE STATUS
              </Typography>
              <Box
                sx={{
                  maxHeight: 160,
                  overflowY: activeStatuses.length > 4 ? "auto" : "unset",
                  mt: 1,
                  pr: 1,
                }}
              >
                <FormGroup>
                  {activeStatuses.map((status) => (
                    <FormControlLabel
                      key={status}
                      control={
                        <Checkbox
                          checked={selectedActiveStatuses.includes(status)}
                          onChange={() => handleActiveStatusChange(status)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">{status}</Typography>}
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Box>

            {/* Rating Filter */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                RATING
              </Typography>
              <Box
                sx={{
                  maxHeight: 160,
                  overflowY: "auto",
                  mt: 1,
                  pr: 1,
                }}
              >
                <FormGroup>
                  {ratingRanges.map((range) => (
                    <FormControlLabel
                      key={range.label}
                      control={
                        <Checkbox
                          checked={selectedRatingRanges.includes(range.label)}
                          onChange={() => handleRatingRangeChange(range.label)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">{range.label}</Typography>
                      }
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Products</Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setCreatingProduct(true)}
            >
              + Add Product
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              border: "1px solid #ddd",
              boxShadow: "none",
            }}
          >
            <Table sx={{ borderCollapse: "separate" }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Image</b>
                  </TableCell>
                  <TableCell>
                    <b>Title</b>
                  </TableCell>
                  <TableCell>
                    <b>Price</b>
                  </TableCell>
                  <TableCell>
                    <b>Active</b>
                  </TableCell>
                  <TableCell>
                    <b>Rating</b>
                  </TableCell>
                  <TableCell>
                    <b>Tool</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredProducts.map((product) => {
                  const imageUrl =
                    product.image ||
                    (Array.isArray(product.images) ? product.images[0] : null);

                  return (
                    <TableRow key={product._id} hover>
                      {/* IMAGE */}
                      <TableCell>
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={product.title}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 1,
                              border: "1px solid #ddd",
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No image
                          </Typography>
                        )}
                      </TableCell>

                      {/* TITLE */}
                      <TableCell>{product.title}</TableCell>

                      {/* PRICE */}
                      <TableCell>{product.price}</TableCell>

                      {/* ACTIVE */}
                      <TableCell>
                        {product.isAuction ? (
                          <Chip label="Active" color="success" size="small" />
                        ) : (
                          <Chip label="Inactive" color="error" size="small" />
                        )}
                      </TableCell>

                      {/* RATING */}
                      <TableCell>
                        {product.averageRating
                          ? product.averageRating.toFixed(1)
                          : "N/A"}{" "}
                        ({product.totalReviews || 0} reviews)
                      </TableCell>

                      {/* TOOL */}
                      <TableCell>
                        <Tooltip title="Update">
                          <IconButton
                            color="primary"
                            onClick={() => setEditingProduct(product)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => setDeletingProduct(product)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="View Reviews">
                          <IconButton
                            color="info"
                            onClick={() => handleViewReviews(product)}
                          >
                            <CommentIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Pagination
              page={currentPage}
              count={totalPages} //  LẤY TỪ BACKEND
              onChange={(e, page) => onPageChange(page)}
              showFirstButton
              showLastButton
              sx={{ display: "flex", justifyContent: "center" }}
            />
          </Stack>

          {editingProduct && (
            <UpdateProduct
              targetProduct={editingProduct}
              onUpdated={() => {
                onProductUpdated(currentPage);
                setEditingProduct(null);
              }}
              open={Boolean(editingProduct)}
              handleClose={() => setEditingProduct(null)}
            />
          )}

          {creatingProduct && (
            <CreateProduct
              open={creatingProduct}
              handleClose={() => setCreatingProduct(false)}
              onCreated={() => {
                onProductUpdated(1); // reload lại list
                setSnackbar({
                  open: true,
                  msg: "Product created successfully!",
                  severity: "success",
                });
                setCreatingProduct(false);
              }}
            />
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
